import React, { useEffect, useState } from 'react';
import { getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '../api/notification';
import { format } from 'date-fns';
import { BellIcon, CheckCircledIcon, Cross1Icon } from "@radix-ui/react-icons";
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { cn } from '../utils/cn';
import Layout from '../components/common/Layout';
import { useToast } from '../hooks/useToast';
import { acceptGroupInvitation, rejectGroupInvitation } from '../api/group';

const NotificationsContent = () => {
  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications(page);
      if (response.notifications) {
        setNotifications(prev => 
          page === 0 ? response.notifications : [...prev, ...response.notifications]
        );
        setHasMore(page < response.totalPages - 1);
        setTotalPages(response.totalPages);
        setUnreadCount(response.unreadCount);
        setPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleAcceptInvitation = async (notification) => {
    try {
      await acceptGroupInvitation(notification.referenceId);
      // Update the notification status locally
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, attributes: { ...n.attributes, status: 'ACCEPTED' } } : n)
      );
      toast({
        title: "Success",
        description: "You have joined the group successfully!",
        type: "success"
      });
      // Navigate to the group page
      navigate(`/group/${notification.referenceId}/view`);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to accept invitation',
        type: "error"
      });
    }
  };

  const handleRejectInvitation = async (notification) => {
    try {
      await rejectGroupInvitation(notification.referenceId);
      // Update the notification status locally
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, attributes: { ...n.attributes, status: 'REJECTED' } } : n)
      );
      toast({
        title: "Success",
        description: "You have declined the group invitation",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to reject invitation',
        type: "error"
      });
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read && notification.id) {
        await markNotificationAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Handle navigation based on notification type
      if (notification.referenceType === 'ACTIVITY' && notification.referenceId) {
        navigate(`/activities/${notification.referenceId}`);
      } else if (notification.referenceType === 'GROUP' && notification.referenceId && notification.attributes?.status === 'ACCEPTED') {
        navigate(`/group/${notification.referenceId}/view`);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const renderInvitationActions = (notification) => {
    // Only show actions for group invitations
    if (notification.type !== 'GROUP_INVITATION') return null;

    const status = notification.attributes?.status;
    
    if (status === 'ACCEPTED') {
      return (
        <div className="flex items-center gap-1 text-primary text-sm">
          <CheckCircledIcon className="h-4 w-4" />
          <span>Accepted</span>
        </div>
      );
    }
    
    if (status === 'REJECTED') {
      return (
        <div className="flex items-center gap-1 text-destructive text-sm">
          <Cross1Icon className="h-4 w-4" />
          <span>Declined</span>
        </div>
      );
    }
    
    // Default to showing actions if no status (treat as PENDING)
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleAcceptInvitation(notification);
          }}
        >
          Accept
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleRejectInvitation(notification);
          }}
        >
          Decline
        </Button>
      </div>
    );
  };

  const renderNotification = (notification) => {
    const formattedDate = format(new Date(notification.createdAt), 'MMM d, yyyy h:mm a');

    return (
      <div
        key={notification.id || notification.createdAt}
        className={cn(
          "group p-4 cursor-pointer border-b border-border",
          "transition-colors hover:bg-accent",
          !notification.read && "bg-primary/5"
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className={cn(
              "p-2 rounded-full",
              !notification.read ? "bg-primary/10" : "bg-muted",
              "group-hover:bg-background transition-colors"
            )}>
              <BellIcon className={cn(
                "h-4 w-4",
                !notification.read ? "text-primary" : "text-muted-foreground"
              )} />
            </div>
          </div>
          <div className="flex-1 min-w-0" onClick={() => handleNotificationClick(notification)}>
            <p className={cn(
              "text-sm font-medium",
              !notification.read ? "text-foreground" : "text-muted-foreground"
            )}>
              {notification.title}
            </p>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {notification.content}
            </p>
            <p className="mt-1 text-xs text-muted-foreground/60">
              {formattedDate}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {notification.type === 'GROUP_INVITATION' && renderInvitationActions(notification)}
            {!notification.read && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNotificationClick(notification);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                Mark as read
              </Button>
            )}
            {notification.read && notification.type !== 'GROUP_INVITATION' && (
              <div className="flex-shrink-0 flex items-center">
                <CheckCircledIcon className="h-4 w-4 text-primary" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        {notificationPermission === 'denied' && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/50 dark:border-yellow-900 p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  We noticed you haven't enabled push notifications on your browser. Enable notifications to stay updated!
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-muted-foreground">
                You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-muted-foreground hover:text-foreground"
            >
              Mark all as read
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background shadow-sm">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="rounded-full bg-muted p-3 mb-4">
              <BellIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              No notifications yet
            </p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={notifications.length}
            next={fetchNotifications}
            hasMore={hasMore}
            loader={
              <div className="p-4 text-center">
                <div className="animate-pulse text-sm text-muted-foreground">
                  Loading more...
                </div>
              </div>
            }
            endMessage={
              <div className="p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  You're all caught up!
                </p>
              </div>
            }
          >
            {notifications.map(renderNotification)}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

const NotificationsPage = () => {
  return (
    <Layout>
      <NotificationsContent />
    </Layout>
  );
};

export default NotificationsPage; 